import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function TiltCard() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 140, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 140, damping: 20 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{ y, perspective: 1200 }}
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
      className="relative"
      data-testid="hero-showcase"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-20px_rgba(16,42,67,0.35)] border-4 border-white"
      >
        <img
          src={IMAGES.hero}
          alt="A thoughtfully curated resort stay at golden hour"
          className="h-[420px] sm:h-[520px] w-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between" style={{ transform: "translateZ(40px)" }}>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] text-ivory/70 uppercase">32.7765° N — 79.9311° W</p>
            <p className="font-serif text-2xl text-ivory mt-1">Charleston, SC</p>
          </div>
          <span className="rounded-full bg-ivory/15 backdrop-blur-md border border-ivory/30 px-4 py-1.5 text-[11px] tracking-[0.14em] uppercase text-ivory">
            Editor's pick
          </span>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
        className="absolute -left-4 sm:-left-10 top-8 rounded-2xl bg-white/90 backdrop-blur-xl border border-slateblue/15 shadow-xl px-5 py-4"
        style={{ transform: "translateZ(60px)" }}
        data-testid="hero-stat-stays"
      >
        <p className="font-serif text-3xl text-navy">240+</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-slateblue mt-0.5">Stays vetted</p>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
        className="absolute -right-3 sm:-right-8 bottom-16 rounded-2xl bg-navy text-ivory shadow-xl px-5 py-4"
        data-testid="hero-stat-guides"
      >
        <p className="font-serif text-3xl">38</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-ivory/60 mt-0.5">Destination guides</p>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section data-testid="hero-section" className="relative overflow-hidden grain-overlay">
      <div className="absolute top-0 right-0 h-[480px] w-[480px] rounded-full bg-ocean/10 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 left-0 h-[360px] w-[360px] rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-40 pb-20 sm:pb-28 grid lg:grid-cols-2 gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="text-xs tracking-[0.28em] uppercase font-semibold text-gold"
            data-testid="hero-eyebrow"
          >
            A travel-planning publication
          </motion.p>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-navy" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Travel well.</MaskedLine>
            <MaskedLine delay={0.4}>
              Stay <span className="italic text-ocean">smarter.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
            className="mt-7 text-base sm:text-lg leading-relaxed text-charcoal/80 max-w-lg"
            data-testid="hero-subtext"
          >
            Thoughtful destination guides, trusted hotel recommendations, and practical planning tools for
            travelers who want every trip to feel worth it.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/destinations"
              data-testid="hero-explore-button"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold text-ivory transition-colors duration-300 hover:bg-ocean"
            >
              Explore Destinations
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/stays"
              data-testid="hero-stays-button"
              className="inline-flex items-center gap-2 rounded-full border border-navy/25 px-8 py-4 text-sm font-semibold text-navy transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Find Your Next Stay
            </Link>
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-8 flex items-start gap-2.5 text-xs leading-relaxed text-slateblue max-w-md"
            data-testid="hero-trust-statement"
          >
            <ShieldCheck className="h-4 w-4 text-gold shrink-0 mt-0.5" />
            Venture &amp; Stay independently researches travel recommendations to help you make confident
            choices. When you book through selected links, we may earn a commission at no additional cost to you.
          </motion.p>
        </div>
        <TiltCard />
      </div>
    </section>
  );
}
